// src/admin/components/PostEditor.jsx
import { useTheme } from "../../context/ThemeContext";
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { useEffect } from 'react';
import FloatingMenuBar from './editor/FloatingMenuBar';
import MenuBar from './editor/MenuBar'; // Sticky toolbar

export default function PostEditor({ formData, updateField }) {
  const { isDark } = useTheme();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Image.configure({
        allowBase64: true,
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg my-2',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-800 underline hover:text-blue-700',
        },
      }),
    ],
    content: formData.content || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      updateField('content', html);
    },
  });

  useEffect(() => {
    if (editor && formData.content !== undefined) {
      const currentContent = editor.getHTML();
      if (formData.content !== currentContent) {
        editor.commands.setContent(formData.content || '');
      }
    }
  }, [editor, formData.content]);

  const inputClass = `
    w-full
    px-4
    py-4
    rounded-2xl
    border
    outline-none
    transition-all
    ${
      isDark
        ? "bg-white/5 border-white/10 focus:border-blue-800/50 text-white placeholder:text-gray-500"
        : "bg-white border-gray-300 focus:border-blue-800 text-gray-900 placeholder:text-gray-400"
    }
  `;

  return (
    <div className="space-y-6">
      <input
        type="text"
        value={formData.title || ''}
        onChange={(e) => updateField("title", e.target.value)}
        className={inputClass}
        placeholder="Post Title"
      />

      <textarea
        rows={4}
        value={formData.excerpt || ''}
        onChange={(e) => updateField("excerpt", e.target.value)}
        className={`${inputClass} resize-y`}
        placeholder="Post Excerpt"
      />

      <div>
        <label className="block mb-2 text-sm font-medium">Content</label>
        <div className={`rounded-2xl overflow-hidden border ${isDark ? 'border-white/10' : 'border-gray-300'}`}>
          {/* Floating Menu - appears on selection */}
          <FloatingMenuBar editor={editor} isDark={isDark} />
          
          {/* Sticky Toolbar - always visible */}
          <MenuBar editor={editor} isDark={isDark} />
          
          <EditorContent 
            editor={editor} 
            className={`
              min-h-[500px] focus:outline-none p-4
              ${isDark ? 'text-gray-200' : 'text-gray-900'}
              prose prose-sm max-w-none
              ${isDark ? 'prose-invert' : ''}
              [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg [&_img]:my-2
              [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:my-4
              [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:my-3
              [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:my-2
              [&_p]:my-2 [&_p]:leading-relaxed
              [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2
              [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2
              [&_li]:my-1
              [&_blockquote]:border-l-4 [&_blockquote]:border-l-blue-800 [&_blockquote]:pl-4 [&_blockquote]:my-2 [&_blockquote]:italic
              [&_pre]:bg-gray-900 [&_pre]:text-gray-100 [&_pre]:p-4 [&_pre]:rounded [&_pre]:my-2 [&_pre]:overflow-x-auto
              [&_code]:bg-gray-200 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm
              ${isDark ? '[&_code]:bg-gray-700 [&_code]:text-gray-200' : '[&_code]:bg-gray-100 [&_code]:text-gray-800'}
              [&_a]:text-blue-800 [&_a]:underline hover:[&_a]:text-blue-700
            `}
          />
        </div>
        <p className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Select text to format it with the floating toolbar, or use the sticky toolbar above.
        </p>
      </div>
    </div>
  );
}